// frontend/src/pages/inventaire/ventesHistorique.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Stack
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as CashIcon,
  LocalAtm as AtmIcon,
  Refresh as RefreshIcon,
  ShoppingCart as ShoppingCartIcon,
  Store as StoreIcon,
  Article as ArticleIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import frLocale from 'date-fns/locale/fr';
import api from '../../services/auth';
import { formatCurrency, formatDate, formatPhoneNumber } from '../../../src/components/utils/formatters';
// import { useReactToPrint } from 'react-to-print';

const VentesHistoriquePage = () => {
  const [ventes, setVentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalVentes, setTotalVentes] = useState(0);
  
  // Recherche et filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [dateDebut, setDateDebut] = useState(null);
  const [dateFin, setDateFin] = useState(null);
  const [methodePaiement, setMethodePaiement] = useState('tous');
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  
  // Détails vente
  const [selectedVente, setSelectedVente] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [venteDetails, setVenteDetails] = useState(null);
  
  // Stats
  const [stats, setStats] = useState({
    total_ventes: 0,
    montant_total: 0,
    moyenne_panier: 0,
    paiements: {}
  });

  // Debounce recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Chargement des ventes
  useEffect(() => {
    fetchVentes();
  }, [page, rowsPerPage, debouncedSearchTerm, dateDebut, dateFin, methodePaiement]);

  // Calcul des stats
  useEffect(() => {
    if (Array.isArray(ventes) && ventes.length > 0) {
      const montantTotal = ventes.reduce((sum, v) => sum + parseFloat(v.montant_total || 0), 0);
      const statsPaiements = ventes.reduce((acc, v) => {
        const methode = v.methode_paiement || 'autres';
        acc[methode] = (acc[methode] || 0) + parseFloat(v.montant_total || 0);
        return acc;
      }, {});
      
      setStats({
        total_ventes: ventes.length,
        montant_total: montantTotal,
        moyenne_panier: ventes.length > 0 ? montantTotal / ventes.length : 0,
        paiements: statsPaiements
      });
    } else {
      setStats({
        total_ventes: 0,
        montant_total: 0,
        moyenne_panier: 0,
        paiements: {}
      });
    }
  }, [ventes]);

  const fetchVentes = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        search: debouncedSearchTerm || undefined
      };
      
      if (dateDebut) {
        params.date_debut = dateDebut.toISOString().split('T')[0];
      }
      if (dateFin) {
        params.date_fin = dateFin.toISOString().split('T')[0];
      }
      if (methodePaiement !== 'tous') {
        params.methode_paiement = methodePaiement;
      }
      
      const response = await api.get('depot/ventes/', { params });
      
      // Gestion des différents formats de réponse API
      if (response.data && Array.isArray(response.data.results)) {
        setVentes(response.data.results);
        setTotalVentes(response.data.count || response.data.results.length);
      } else if (Array.isArray(response.data)) {
        setVentes(response.data);
        setTotalVentes(response.data.length);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setVentes(response.data.data);
        setTotalVentes(response.data.total || response.data.data.length);
      } else {
        setVentes([]);
        setTotalVentes(0);
        console.error('Format de réponse inattendu:', response.data);
      }
    } catch (err) {
      console.error('Erreur chargement ventes:', err);
      setError('Impossible de charger l\'historique des ventes');
      setVentes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (vente) => {
    setSelectedVente(vente);
    setOpenDetailsDialog(true);
    setDetailsLoading(true);
    setVenteDetails(null);
    
    try {
      // Essayer d'abord avec l'endpoint récépissé
      const response = await api.get(`depot/ventes/${vente.id}/recu/`);
      setVenteDetails(response.data);
    } catch (err) {
      console.error('Erreur chargement détails vente:', err);
      // Fallback: utiliser les données de la vente elle-même
      setVenteDetails(vente);
      setError('Impossible de charger les détails complets de la vente');
    } finally {
      setDetailsLoading(false);
    }
  };

  // const handlePrint = useReactToPrint({
  //   content: () => document.getElementById('ticket-print'),
  //   pageStyle: '@page { size: 80mm 200mm; margin: 0; }'
  // });

  const handleResetFilters = () => {
    setDateDebut(null);
    setDateFin(null);
    setMethodePaiement('tous');
    setSearchTerm('');
    setPage(0);
  };

  const getPaiementIcon = (methode) => {
    switch (methode?.toLowerCase()) {
      case 'espèces':
      case 'especes':
        return <CashIcon />;
      case 'carte':
        return <CreditCardIcon />;
      case 'crédit':
      case 'credit':
        return <AtmIcon />;
      default:
        return <MoneyIcon />;
    }
  };

  const getPaiementColor = (methode) => {
    switch (methode?.toLowerCase()) {
      case 'espèces':
      case 'especes':
        return 'success';
      case 'carte':
        return 'primary';
      case 'crédit':
      case 'credit':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Fonction pour obtenir le nom du vendeur
  const getVendeurNom = (vente) => {
    // Priorité : caissier_nom (dans vos données) > vendeur_nom > N/A
    return vente.caissier_nom || vente.vendeur_nom || 'N/A';
  };

  // Vérifier si des filtres sont actifs
  const hasActiveFilters = () => {
    return dateDebut || dateFin || methodePaiement !== 'tous' || searchTerm;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
      <Box sx={{ p: 3 }}>
        {/* Header avec style comme Succursale */}
        <Card sx={{ p: 3, mb: 3, boxShadow: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Historique des ventes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.total_ventes || 0} vente(s) trouvée(s)
              </Typography>
            </Box>
            
            <Box display="flex" gap={2}>
              <Tooltip title="Actualiser les données">
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchVentes}
                  disabled={loading}
                  sx={{ borderRadius: '8px' }}
                >
                  Actualiser
                </Button>
              </Tooltip>
            </Box>
          </Box>

          {/* Cartes statistiques - Style amélioré */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ boxShadow: 2, borderRadius: '12px' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total ventes
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {stats.total_ventes || 0}
                      </Typography>
                    </Box>
                    {/* <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                      <ReceiptIcon />
                    </Avatar> */}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ boxShadow: 2, borderRadius: '12px' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Montant total
                      </Typography>
                      <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(stats.montant_total || 0)}
                      </Typography>
                    </Box>
                    {/* <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                      <MoneyIcon />
                    </Avatar> */}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ boxShadow: 2, borderRadius: '12px' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Panier moyen
                      </Typography>
                      <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(stats.moyenne_panier || 0)}
                      </Typography>
                    </Box>
                    {/* <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                      <ShoppingCartIcon />
                    </Avatar> */}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ boxShadow: 2, borderRadius: '12px' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Répartition paiements
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                    {stats.paiements && Object.keys(stats.paiements).length > 0 ? (
                      Object.entries(stats.paiements).map(([methode, montant]) => (
                        <Tooltip key={methode} title={`${methode}: ${formatCurrency(montant)}`}>
                          <Chip
                            // icon={getPaiementIcon(methode)}
                            label={methode}
                            size="small"
                            color={getPaiementColor(methode)}
                            variant="outlined"
                            sx={{ fontWeight: 'medium' }}
                          />
                        </Tooltip>
                      ))
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Aucune donnée
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* SECTION FILTRES CORRIGÉE - Style complètement intégré */}
          <Box sx={{ width: '100%' }}>
            <Box display="flex" alignItems="center" mb={2}>
              <FilterIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                Filtres
              </Typography>
              {hasActiveFilters() && (
                <Chip
                  label="Filtres actifs"
                  size="small"
                  color="primary"
                  sx={{ ml: 2 }}
                />
              )}
            </Box>
            
            <Grid container spacing={2} alignItems="center">
              {/* Recherche */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Rechercher par référence..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  variant="outlined"
                />
              </Grid>

              {/* Date début */}
              <Grid item xs={12} md={2.5}>
                <DatePicker
                  label="Date début"
                  value={dateDebut}
                  onChange={(newValue) => setDateDebut(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      variant: 'outlined',
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }
                    }
                  }}
                />
              </Grid>

              {/* Date fin */}
              <Grid item xs={12} md={2.5}>
                <DatePicker
                  label="Date fin"
                  value={dateFin}
                  onChange={(newValue) => setDateFin(newValue)}
                  minDate={dateDebut || undefined}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      variant: 'outlined',
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }
                    }
                  }}
                />
              </Grid>

              {/* Méthode de paiement */}
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Méthode de paiement</InputLabel>
                  <Select
                    value={methodePaiement}
                    onChange={(e) => setMethodePaiement(e.target.value)}
                    label="Méthode de paiement"
                    startAdornment={
                      <InputAdornment position="start">
                        {/* {getPaiementIcon(methodePaiement)} */}
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="tous">Tous</MenuItem>
                    <MenuItem value="espèces">Espèces</MenuItem>
                    <MenuItem value="carte">Carte bancaire</MenuItem>
                    <MenuItem value="crédit">Crédit</MenuItem>
                    <MenuItem value="autres">Autre</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Bouton réinitialiser */}
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  onClick={handleResetFilters}
                  disabled={!hasActiveFilters()}
                  startIcon={<ClearIcon />}
                  sx={{ 
                    borderRadius: '8px', 
                    height: '40px'
                  }}
                >
                  Réinitialiser
                </Button>
              </Grid>
            </Grid>

            {/* Affichage des filtres actifs */}
            {hasActiveFilters() && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {dateDebut && (
                  <Chip
                    size="small"
                    label={`Du: ${formatDate(dateDebut)}`}
                    onDelete={() => setDateDebut(null)}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {dateFin && (
                  <Chip
                    size="small"
                    label={`Au: ${formatDate(dateFin)}`}
                    onDelete={() => setDateFin(null)}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {methodePaiement !== 'tous' && (
                  <Chip
                    size="small"
                    icon={getPaiementIcon(methodePaiement)}
                    label={`Paiement: ${methodePaiement}`}
                    onDelete={() => setMethodePaiement('tous')}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {searchTerm && (
                  <Chip
                    size="small"
                    icon={<SearchIcon />}
                    label={`Recherche: ${searchTerm}`}
                    onDelete={() => setSearchTerm('')}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          </Box>
        </Card>

        {/* Tableau des ventes - Style amélioré comme Succursale */}
        <Card sx={{ boxShadow: 3, borderRadius: '12px', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Référence</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Vendeur</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Client</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Montant</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Paiement</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Articles</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography color="text.secondary">
                          Chargement des ventes...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : !Array.isArray(ventes) || ventes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        {searchTerm || dateDebut || dateFin || methodePaiement !== 'tous' 
                          ? 'Aucune vente ne correspond à vos critères' 
                          : 'Aucune vente trouvée'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  ventes.map((vente) => (
                    <TableRow 
                      key={vente.id || Math.random()} 
                      hover
                      sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <TableCell>
                        <Chip 
                          label={vente.reference || 'N/A'} 
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <CalendarIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {vente.date_vente ? formatDate(vente.date_vente) : 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {getVendeurNom(vente)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {vente.client_nom || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                          {formatCurrency(vente.montant_total || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getPaiementIcon(vente.methode_paiement)}
                          label={vente.methode_paiement || 'N/A'}
                          size="small"
                          color={getPaiementColor(vente.methode_paiement)}
                          variant="outlined"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<ArticleIcon fontSize="small" />}
                          label={vente.nombre_articles || (vente.articles && Array.isArray(vente.articles) ? vente.articles.length : 0)}
                          size="small"
                          variant="outlined"
                          color="info"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Voir détails">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(vente)}
                            disabled={detailsLoading}
                            sx={{ 
                              bgcolor: 'info.light', 
                              '&:hover': { bgcolor: 'info.main', color: 'white' }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {ventes.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalVentes || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lignes par page"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`}
              sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}
            />
          )}
        </Card>

        {/* Dialog détails vente - Style amélioré */}
        <Dialog
          open={openDetailsDialog}
          onClose={() => setOpenDetailsDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: '12px' }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <ReceiptIcon sx={{ mr: 1 }} />
              Détails de la vente
            </Typography>
            <IconButton onClick={() => setOpenDetailsDialog(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ mt: 1 }}>
            {detailsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : venteDetails ? (
              <Box id="ticket-print" sx={{ p: 3, maxWidth: '500px', mx: 'auto' }}>
                {/* En-tête entreprise */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  {venteDetails.entreprise_logo && (
                    <img 
                      src={venteDetails.entreprise_logo} 
                      alt="Logo" 
                      style={{ maxWidth: '80px', maxHeight: '80px', marginBottom: '8px' }}
                    />
                  )}
                  <Typography variant="h6" fontWeight="bold">
                    {venteDetails.entreprise_nom || 'Entreprise'}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {venteDetails.entreprise_telephone || ''}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {venteDetails.entreprise_adresse || ''}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                </Box>

                {/* Info vente */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    N° {venteDetails.reference || selectedVente?.reference || 'N/A'}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Date: {venteDetails.date_vente_formatee || formatDate(venteDetails.date_vente || selectedVente?.date_vente) || 'N/A'}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Vendeur: {venteDetails.caissier_nom || venteDetails.vendeur_nom || selectedVente?.caissier_nom || selectedVente?.vendeur_nom || 'N/A'}
                  </Typography>
                  {(venteDetails.client_nom || selectedVente?.client_nom) && (
                    <Typography variant="caption" display="block">
                      Client: {venteDetails.client_nom || selectedVente?.client_nom}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block">
                    Paiement: {venteDetails.methode_paiement || selectedVente?.methode_paiement || 'N/A'}
                  </Typography>
                </Box>

                <Divider />

                {/* Lignes de vente */}
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Articles vendus:</Typography>
                <Table size="small" sx={{ mt: 1, mb: 1 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.light.100' }}>
                      <TableCell sx={{ p: 0.5, fontWeight: 'bold' }}>Article</TableCell>
                      <TableCell sx={{ p: 0.5, fontWeight: 'bold' }} align="right">Qté</TableCell>
                      <TableCell sx={{ p: 0.5, fontWeight: 'bold' }} align="right">P.U.</TableCell>
                      <TableCell sx={{ p: 0.5, fontWeight: 'bold' }} align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {venteDetails.lignes && Array.isArray(venteDetails.lignes) && venteDetails.lignes.length > 0 ? (
                      venteDetails.lignes.map((ligne, index) => (
                        <TableRow key={ligne.id || index}>
                          <TableCell sx={{ p: 0.5 }}>
                            <Typography variant="caption">
                              {ligne.produit_nom || ligne.article_nom || 'Produit'}
                              {ligne.conditionnement && ` (${ligne.conditionnement})`}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }} align="right">
                            {ligne.quantite_totale || ligne.quantite || 0}
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }} align="right">
                            {formatCurrency(ligne.prix_unitaire || 0)}
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }} align="right">
                            {formatCurrency(ligne.total || 0)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : selectedVente?.lignes && Array.isArray(selectedVente.lignes) ? (
                      selectedVente.lignes.map((ligne, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ p: 0.5 }}>
                            <Typography variant="caption">
                              {ligne.produit_nom || 'Produit'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }} align="right">
                            {ligne.quantite || 0}
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }} align="right">
                            {formatCurrency(ligne.prix_unitaire || 0)}
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }} align="right">
                            {formatCurrency(ligne.total || 0)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            Aucun article dans cette vente
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                <Divider sx={{ my: 1 }} />

                {/* Totaux */}
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Sous-total:</Typography>
                    <Typography variant="body2">{formatCurrency(venteDetails.sous_total || selectedVente?.sous_total || 0)}</Typography>
                  </Box>
                  {(venteDetails.remise > 0 || selectedVente?.remise > 0) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Remise:</Typography>
                      <Typography variant="body2" color="error.main">
                        -{formatCurrency(venteDetails.remise || selectedVente?.remise || 0)}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold">Total:</Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                      {formatCurrency(venteDetails.montant_total || selectedVente?.montant_total || 0)}
                    </Typography>
                  </Box>
                  {(venteDetails.montant_recu > 0 || selectedVente?.montant_recu > 0) && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption">Montant reçu:</Typography>
                        <Typography variant="caption">{formatCurrency(venteDetails.montant_recu || selectedVente?.montant_recu || 0)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption">Monnaie:</Typography>
                        <Typography variant="caption" color="success.main">
                          {formatCurrency(venteDetails.monnaie || selectedVente?.monnaie || 0)}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Typography color="textSecondary">
                  Aucun détail disponible
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
            <Button 
              onClick={() => setOpenDetailsDialog(false)}
              variant="outlined"
              sx={{ borderRadius: '8px' }}
            >
              Fermer
            </Button>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              disabled={!venteDetails}
              // onClick={handlePrint}
              sx={{ borderRadius: '8px' }}
            >
              Imprimer
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notifications */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setSuccess(null)} severity="success" variant="filled" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default VentesHistoriquePage;